import { authService } from './auth';
import { headerService } from './headers';
import { storageService } from './storage';

// Interfaz para la información del token almacenada localmente
interface TokenInfo {
  expirationTime: string; // ISO string
  issuedAt: string; // ISO string
  lastRefreshTime: string; // ISO string
  refreshThreshold: number; // minutos antes de expirar para refrescar
}

// Clase para manejar el refresh inteligente de tokens
export class TokenRefreshService {
  private static instance: TokenRefreshService;
  private readonly STORAGE_KEY = 'token_info';
  private readonly DEFAULT_REFRESH_THRESHOLD = 5; // 5 minutos antes de expirar

  private constructor() {}

  public static getInstance(): TokenRefreshService {
    if (!TokenRefreshService.instance) {
      TokenRefreshService.instance = new TokenRefreshService();
    }
    return TokenRefreshService.instance;
  }

  /**
   * Verifica si el token necesita ser refrescado basándose en información local
   * @returns true si necesita refresh, false si está bien
   */
  public shouldRefreshToken(): boolean {
    try {
      const tokenInfo = this.getStoredTokenInfo();
      if (!tokenInfo) {
        console.log('🔍 No hay información de token almacenada, necesita refresh');
        return true;
      }

      const now = new Date();
      const expirationTime = new Date(tokenInfo.expirationTime);
      const timeUntilExpiration = expirationTime.getTime() - now.getTime();
      const minutesUntilExpiration = timeUntilExpiration / (1000 * 60);

      console.log(`🔍 Token expira en ${minutesUntilExpiration.toFixed(1)} minutos`);

      // Si está dentro del umbral de refresh, necesitamos refrescar
      if (minutesUntilExpiration <= tokenInfo.refreshThreshold) {
        console.log(`🔄 Token necesita refresh (umbral: ${tokenInfo.refreshThreshold} min)`);
        return true;
      }

      console.log('✅ Token está bien, no necesita refresh');
      return false;
    } catch (error) {
      console.error('❌ Error verificando token local:', error);
      return true; // En caso de error, mejor refrescar
    }
  }

  /**
   * Refresca el token solo si es necesario
   * @returns true si se refrescó exitosamente, false si no era necesario o falló
   */
  public async refreshTokenIfNeeded(): Promise<boolean> {
    if (!this.shouldRefreshToken()) {
      console.log('⏭️ Token no necesita refresh, saltando...');
      return true; // No necesita refresh, pero está "bien"
    }

    return await this.refreshToken();
  }

  /**
   * Refresca el token de Firebase y actualiza la información local
   * @returns true si se refrescó exitosamente
   */
  public async refreshToken(): Promise<boolean> {
    try {
      console.log('🔄 Iniciando refresh de token...');
      
      const currentUser = authService.auth.currentUser;
      if (!currentUser) {
        console.log('❌ No hay usuario autenticado para refrescar token');
        return false;
      }

      // Forzar refresh del token en Firebase
      await currentUser.getIdToken(true);
      const newToken = await currentUser.getIdToken();
      
      // Obtener información actualizada del token
      const tokenResult = await currentUser.getIdTokenResult();
      
      // Guardar información del token localmente
      const tokenInfo: TokenInfo = {
        expirationTime: tokenResult.expirationTime,
        issuedAt: tokenResult.issuedAtTime,
        lastRefreshTime: new Date().toISOString(),
        refreshThreshold: this.DEFAULT_REFRESH_THRESHOLD
      };
      
      this.storeTokenInfo(tokenInfo);
      
      // Actualizar token en headerService
      headerService.setAuthToken(newToken);
      
      // Actualizar storage con los nuevos claims si están disponibles
      const customClaims = tokenResult.claims as any;
      if (customClaims.otsUser) {
        const userDataWithId = {
          ...customClaims.otsUser,
          id: currentUser.uid
        };
        await storageService.saveUser(userDataWithId);
        console.log('💾 Storage actualizado con claims del backend');
      }
      
      console.log('✅ Token refrescado exitosamente');
      return true;
      
    } catch (error) {
      console.error('❌ Error refrescando token:', error);
      
      // Si falla el refresh, limpiar información
      this.clearStoredTokenInfo();
      headerService.clearAuthToken();
      await storageService.clearUser();
      
      return false;
    }
  }

  /**
   * Inicializa la información del token después del login
   * @param tokenResult Resultado del token de Firebase
   */
  public initializeTokenInfo(tokenResult: any): void {
    const tokenInfo: TokenInfo = {
      expirationTime: tokenResult.expirationTime,
      issuedAt: tokenResult.issuedAtTime,
      lastRefreshTime: new Date().toISOString(),
      refreshThreshold: this.DEFAULT_REFRESH_THRESHOLD
    };
    
    this.storeTokenInfo(tokenInfo);
    console.log('💾 Información de token inicializada');
  }

  /**
   * Obtiene información del token almacenada localmente
   */
  private getStoredTokenInfo(): TokenInfo | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('❌ Error obteniendo información de token:', error);
      return null;
    }
  }

  /**
   * Guarda información del token localmente
   */
  private storeTokenInfo(tokenInfo: TokenInfo): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tokenInfo));
    } catch (error) {
      console.error('❌ Error guardando información de token:', error);
    }
  }

  /**
   * Limpia la información del token almacenada
   */
  private clearStoredTokenInfo(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('❌ Error limpiando información de token:', error);
    }
  }

  /**
   * Obtiene información de debug del token
   */
  public getTokenDebugInfo(): any {
    const tokenInfo = this.getStoredTokenInfo();
    if (!tokenInfo) {
      return { status: 'No hay información de token' };
    }

    const now = new Date();
    const expirationTime = new Date(tokenInfo.expirationTime);
    const timeUntilExpiration = expirationTime.getTime() - now.getTime();
    const minutesUntilExpiration = timeUntilExpiration / (1000 * 60);

    return {
      status: 'Token almacenado',
      expirationTime: tokenInfo.expirationTime,
      minutesUntilExpiration: Math.floor(minutesUntilExpiration),
      lastRefreshTime: tokenInfo.lastRefreshTime,
      refreshThreshold: tokenInfo.refreshThreshold,
      needsRefresh: this.shouldRefreshToken()
    };
  }
}

// Instancia global del servicio
export const tokenRefreshService = TokenRefreshService.getInstance(); 